"use client";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { MethodTypeEnum, ModuleTypeEnum } from "@/enum";
import { ITool } from "@/actions/ai-configuration/tool/types";
import {
  UpdateAiConfigurationData,
  UpdateAiConfigurationFormSchema,
} from "@/components/shared/Forms/UpdateAiConfigurationForm/schema";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import EditorToolModal from "@/components/layouts/ToolModals/EditorToolModal";
import { v4 as uuidv4 } from "uuid";
import { Check, List, MoreVertical, Pencil, Plus, X } from "lucide-react";
import DetailToolModal from "@/components/layouts/ToolModals/DetailToolModal";
import { useTranslations } from "next-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface IUpdateAiConfigurationFormProps {
  aiConfigurationId: string;
  apiUrl: string;
  description: string;
  type: ModuleTypeEnum;
  tools?: ITool[];
  onSuccess?: () => void;
}

const UpdateAiConfigurationForm = ({
  aiConfigurationId,
  apiUrl,
  description,
  type,
  onSuccess,
  tools,
}: IUpdateAiConfigurationFormProps) => {
  const { toast } = useToast();

  const t = useTranslations("AiConfiguration");

  const form = useForm<UpdateAiConfigurationData>({
    resolver: zodResolver(UpdateAiConfigurationFormSchema),
    defaultValues: {
      aiConfigurationId: aiConfigurationId,
      apiUrl: apiUrl,
      description: description,
      type: type,
      tools: tools || [],
    },
  });

  const toolModel = {
    id: uuidv4(),
    name: "",
    apiUrl: "",
    description: "",
    methodType: MethodTypeEnum.GET,
    type: ModuleTypeEnum.CUSTOMER,
    deleted_at: null,
    updated_at: null,
  } as ITool;

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "tools",
    keyName: "_fieldId",
  });

  const updateIntegrationService = hooks.useUpdateAiConfiguration({
    onSuccess: () => {
      toast({
        title: "Configuração atualizada com sucesso!",
        variant: "success",
      });
      if (onSuccess) onSuccess?.();
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível atualizar configuração",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: UpdateAiConfigurationData) {
    await updateIntegrationService.mutateAsync({
      ...data,
      tools: data.tools?.map((t) => ({
        id: t.id,
        name: t.name,
        apiUrl: t.apiUrl,
        description: t.description,
        methodType: t.methodType,
        type: t.type,
        updated_at: t.updated_at ?? null,
        deleted_at: t.deleted_at ?? null,
      })),
    });
  }

  function generateDate(): string {
    return new Date().toISOString();
  }

  const [isDetailToolModalOpen, setDetailToolModalOpen] = useState(false);
  const [isEditorToolModalOpen, setEditorToolModalOpen] = useState(false);
  const [viewTool, setViewTool] = useState<ITool | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTool, setEditingTool] = useState<ITool | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState<UpdateAiConfigurationData>(
    form.getValues(),
  );

  const openCreateToolModal = () => {
    setInitialValues(structuredClone(form.getValues()));

    const newIndex = fields.length;
    append(toolModel);

    setIsEditing(false);
    setEditingIndex(newIndex);
    setEditingTool(toolModel);
    setEditorToolModalOpen(true);
  };

  const openEditToolModal = (tool: ITool, index: number) => {
    setInitialValues(structuredClone(form.getValues()));

    setEditingIndex(index);
    setEditingTool(tool);
    setIsEditing(true);
    setEditorToolModalOpen(true);
  };

  const openDetailToolModal = (tool: ITool) => {
    setDetailToolModalOpen(true);
    setViewTool(tool);
  };

  const handleSaveTool = (tool: ITool, index: number) => {
    tool.updated_at = generateDate();
    update(index, tool);
    setInitialValues(form.getValues());
  };

  const deleteTool = (tool: ITool, index: number) => {
    if (!tool.created_at) {
      removeTool(index);
    } else {
      tool.deleted_at = generateDate();
      handleSaveTool(tool, index);
    }
  };

  const activeTool = (tool: ITool, index: number) => {
    tool.deleted_at = null;
    handleSaveTool(tool, index);
  };

  const removeTool = (index: number) => {
    remove(index);
  };

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full flex flex-col items-center gap-y-6"
        >
          <FormFields.InputFormField
            form={form}
            formName="apiUrl"
            placeholder="Insira a URL da configuração"
            label="URL"
          />

          <FormFields.SelectFormField
            form={form}
            formName="type"
            label="Tipo"
            placeholder="Selecione o tipo"
            options={[{ label: "Agente", value: "AGENT" }]}
          />

          <FormFields.TextareaFormField
            form={form}
            formName="description"
            label="Descrição (Prompt)"
            placeholder="Insira a descrição da configuração"
            rows={6}
          />

          <Button
            className="w-full"
            type="button"
            onClick={openCreateToolModal}
          >
            <Plus className={`h-5 w-5 mr-1`} />
            Adicionar Ferramenta
          </Button>

          {fields.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <table className="w-full table-fixed">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <div className="truncate">Nome</div>
                      </div>
                    </th>

                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <div className="truncate">Método</div>
                      </div>
                    </th>

                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <div className="truncate">Tipo</div>
                      </div>
                    </th>

                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <div className="text-center w-full">Ações</div>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {fields.map((field, index) => (
                    <tr
                      key={field.id}
                      className={` border-b border-gray-200 hover:bg-gray-50 transition-colors 
                                        ${field.deleted_at ? "opacity-50 [&>*]:line-through" : ""} `}
                    >
                      <td className="py-3 px-4 truncate max-w-[200px]">
                        {field.name}
                      </td>

                      <td className="py-3 px-4 truncate max-w-[120px]">
                        {field.methodType}
                      </td>

                      <td className="py-3 px-4 truncate max-w-[140px]">
                        {t(`types.${field.type}`)}
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditToolModal(field, index)}
                          >
                            <Pencil />
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openDetailToolModal(field)}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <List className="h-4 w-4" />
                                Detalhes
                              </DropdownMenuItem>

                              {field.deleted_at ? (
                                <DropdownMenuItem
                                  className="flex items-center gap-2 cursor-pointer text-green-600 focus:text-green-600"
                                  onClick={() => activeTool(field, index)}
                                >
                                  <Check className="h-4 w-4" />
                                  Ativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                                  onClick={() => deleteTool(field, index)}
                                >
                                  <X className="h-4 w-4" />
                                  Remover
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-4 text-center text-muted-foreground">
              Nenhuma Ferramenta encontrada.
            </div>
          )}

          <Buttons.DefaultButton
            className="py-6"
            type="submit"
            title="Salvar Alterações"
            isLoading={updateIntegrationService.isPending}
          />
        </form>

        {isEditorToolModalOpen && (
          <EditorToolModal
            isOpen={isEditorToolModalOpen}
            onOpenChange={setEditorToolModalOpen}
            onSave={handleSaveTool}
            index={editingIndex}
            form={form}
            isEditing={isEditing}
            initialValues={initialValues}
            {...editingTool}
          />
        )}

        {isDetailToolModalOpen && (
          <DetailToolModal
            isOpen={isDetailToolModalOpen}
            onOpenChange={setDetailToolModalOpen}
            isError={!viewTool}
            toolData={viewTool}
          />
        )}
      </Form>
    </>
  );
};

export default UpdateAiConfigurationForm;
